import { useCallback } from 'react';
import axios, { AxiosResponse, AxiosError } from 'axios';
import { noop } from 'utils/noop';

type AxiosRequestFn<B, R> = ({
    endpoint,
    body,
    thenFn,
    catchFn,
    finallyFn,
}: {
    endpoint: string;
    body: B;
    thenFn: (response: AxiosResponse<R>) => void;
    catchFn: (error: AxiosError) => void;
    finallyFn?: () => void;
}) => void;

export const useAxios = <B, R>(): {
    axiosPost: AxiosRequestFn<B, R>;
    axiosGet: AxiosRequestFn<B, R>;
} => {
    const axiosPost = useCallback(
        ({
            endpoint,
            body,
            thenFn,
            catchFn,
            finallyFn = noop,
        }: {
            endpoint: string;
            body: B;
            thenFn: (response: AxiosResponse<R>) => void;
            catchFn: (error: AxiosError) => void;
            finallyFn?: () => void;
        }) => {
            axios
                .post<R>(endpoint, body)
                .then(thenFn)
                .catch(catchFn)
                .finally(finallyFn);
        },
        [],
    );

    const axiosGet = useCallback(
        ({
            endpoint,
            body,
            thenFn,
            catchFn,
            finallyFn = noop,
        }: {
            endpoint: string;
            body: B;
            thenFn: (response: AxiosResponse<R>) => void;
            catchFn: (error: AxiosError) => void;
            finallyFn?: () => void;
        }) => {
            axios
                .get<R>(endpoint, body)
                .then(thenFn)
                .catch(catchFn)
                .finally(finallyFn);
        },
        [],
    );

    return { axiosPost, axiosGet };
};
